import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { adminApi } from '../lib/api';
import { copy } from '../lib/copy';
import { ADMIN_PRODUCT, type AdminProductVariant } from '../lib/admin-product';

const RECENT_KEY = 'admin_search_recent';

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed.slice(0, 5) : [];
  } catch {
    return [];
  }
}

function saveRecent(userId: string) {
  const next = [userId, ...loadRecent().filter((id) => id !== userId)].slice(0, 5);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}

function userPath(product: AdminProductVariant, userId: string): string {
  if (product === 'app' || product === 'studio') return `/${product}/users/${userId}`;
  return `/users/${userId}`;
}

export function GlobalSearch({ product = ADMIN_PRODUCT }: { product?: AdminProductVariant }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const usersQuery = useQuery({
    queryKey: ['search', product, 'users', q],
    queryFn: () => {
      const params = { q: q.trim(), limit: 5 };
      if (product === 'app') return adminApi.appUsers(params);
      if (product === 'studio') return adminApi.studioUsers(params);
      return adminApi.users(params);
    },
    enabled: open && q.trim().length >= 2,
  });

  const paymentsQuery = useQuery({
    queryKey: ['search', 'payments', q],
    queryFn: () => adminApi.payments({ q: q.trim(), limit: 5 }),
    enabled: product === 'all' && open && q.trim().length >= 2,
  });

  const go = (path: string, userId?: string) => {
    if (userId) saveRecent(userId);
    setOpen(false);
    setQ('');
    navigate(path);
  };

  if (!open) {
    return (
      <button type="button" className="ops-btn-secondary py-2 text-xs" onClick={() => setOpen(true)}>
        <Search className="h-4 w-4" />
        {copy.search.hint}
      </button>
    );
  }

  const recent = loadRecent();

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4 pt-20">
      <div className="w-full max-w-lg rounded-card border border-ops-border bg-ops-surface shadow-card">
        <div className="border-b border-ops-border p-3">
          <input
            autoFocus
            className="ops-input"
            placeholder={copy.search.placeholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="max-h-80 overflow-auto p-3 text-sm">
          {recent.length > 0 && q.trim().length < 2 ? (
            <div className="mb-3">
              <p className="mb-1 text-xs font-medium text-ops-muted">{copy.search.recent}</p>
              {recent.map((id) => (
                <button
                  key={id}
                  type="button"
                  className="block w-full rounded-pill px-2 py-1 text-left hover:bg-ops-surface-hover"
                  onClick={() => go(userPath(product, id), id)}
                >
                  {id}
                </button>
              ))}
            </div>
          ) : null}

          {usersQuery.data && usersQuery.data.items.length > 0 ? (
            <div className="mb-3">
              <p className="mb-1 text-xs font-medium text-ops-muted">{copy.search.users}</p>
              {usersQuery.data.items.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  className="block w-full rounded-pill px-2 py-1 text-left hover:bg-ops-surface-hover"
                  onClick={() => go(userPath(product, user.id), user.id)}
                >
                  {user.first_name} {user.username ? `(@${user.username})` : ''}
                </button>
              ))}
            </div>
          ) : null}

          {product === 'all' && paymentsQuery.data && paymentsQuery.data.items.length > 0 ? (
            <div className="mb-3">
              <p className="mb-1 text-xs font-medium text-ops-muted">{copy.search.payments}</p>
              {paymentsQuery.data.items.map((payment) => (
                <button
                  key={payment.id}
                  type="button"
                  className="block w-full rounded-pill px-2 py-1 text-left hover:bg-ops-surface-hover"
                  onClick={() => go(`/billing/${payment.id}`)}
                >
                  {payment.order_number}
                </button>
              ))}
            </div>
          ) : null}

          {q.trim().length >= 2 &&
          usersQuery.isFetched &&
          (product !== 'all' || paymentsQuery.isFetched) &&
          (usersQuery.data?.items.length ?? 0) === 0 &&
          (product !== 'all' || (paymentsQuery.data?.items.length ?? 0) === 0) ? (
            <p className="text-ops-muted">{copy.search.noResults}</p>
          ) : null}

          {product !== 'studio' && q.match(/^[0-9a-f-]{36}$/i) ? (
            <button
              type="button"
              className="mt-2 text-ops-text hover:underline"
              onClick={() => go(product === 'app' ? `/${product}/agents/${q.trim()}` : `/agents/${q.trim()}`)}
            >
              {copy.search.agents}: {q.trim()}
            </button>
          ) : null}
        </div>
        <div className="border-t border-ops-border p-2 text-right">
          <button type="button" className="ops-btn-secondary py-1 text-xs" onClick={() => setOpen(false)}>
            {copy.common.hide}
          </button>
        </div>
      </div>
    </div>
  );
}
